import PropTypes from "prop-types";
import { NavIcons } from "@/config/antd";
import "@/assets/styles/slide_out_menu.css";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useTheme } from "@/hooks/ThemeContext";
import { useState } from "react";

// const { Text } = Typography;

const NavIcon = ({ iconName, state }) => {
  const { theme } = useTheme();

  // Use the passed state for the icon (don't force hover in dark mode)
  const iconState = state;

  // In dark mode, we'll apply a filter to make default icons white
  const darkModeStyle =
    theme === "dark" && iconState !== "hover"
      ? { filter: "brightness(0) invert(1)" }
      : {};

  return (
    <img
      src={
        iconState === "hover"
          ? NavIcons[iconName].hover
          : NavIcons[iconName].default
      }
      alt={`${iconName} icon`}
      className="w-8 h-8"
      style={darkModeStyle}
    />
  );
};

NavIcon.propTypes = {
  iconName: PropTypes.oneOf([
    "Campaign",
    "Candidates",
    "Canvass",
    "Finance",
    "Help",
    "Home",
    "Manifesto",
    "Media",
    "Polls",
    "Roles",
    "Settings",
    "Support",
    "Townhall",
    "Users",
  ]),
  isSelected: PropTypes.bool,
  isParentHovered: PropTypes.bool,
  state: PropTypes.oneOf(["default", "hover"]),
};

const menuItems = [
  {
    key: "", // Empty key for the root dashboard URL
    icon: <NavIcon iconName="Home" />,
    icon_hover: <NavIcon iconName="Home" state="hover" />,
    label: "Overview",
  },
  {
    key: "management",
    type: "group",
    label: "Management",
    children: [
      {
        key: "events",
        icon: <NavIcon iconName="Campaign" />,
        icon_hover: <NavIcon iconName="Campaign" state="hover" />,
        label: "Events",
      },
      {
        key: "candidates",
        icon: <NavIcon iconName="Candidates" />,
        icon_hover: <NavIcon iconName="Candidates" state="hover" />,
        label: "Candidates",
      },
      {
        key: "canvass",
        icon: <NavIcon iconName="Canvass" />,
        icon_hover: <NavIcon iconName="Canvass" state="hover" />,
        label: "Canvass",
      },
      {
        key: "townhall",
        icon: <NavIcon iconName="Townhall" />,
        icon_hover: <NavIcon iconName="Townhall" state="hover" />,
        label: "Townhall",
      },
      {
        key: "manifesto",
        icon: <NavIcon iconName="Manifesto" />,
        icon_hover: <NavIcon iconName="Manifesto" state="hover" />,
        label: "Manifesto",
      },
      {
        key: "polls",
        icon: <NavIcon iconName="Polls" />,
        icon_hover: <NavIcon iconName="Polls" state="hover" />,
        label: "Polls and Surveys",
      },
      {
        key: "media",
        icon: <NavIcon iconName="Media" />,
        icon_hover: <NavIcon iconName="Media" state="hover" />,
        label: "Media Manager",
      },
      {
        key: "financial",
        icon: <NavIcon iconName="Finance" />,
        icon_hover: <NavIcon iconName="Finance" state="hover" />,
        label: "Financial Manager",
      },
    ],
  },
  {
    key: "administrative",
    type: "group",
    label: "Administration",
    children: [
      {
        key: "users",
        icon: <NavIcon iconName="Users" />,
        icon_hover: <NavIcon iconName="Users" state="hover" />,
        label: "Team Admin",
      },
      {
        key: "roles",
        icon: <NavIcon iconName="Roles" />,
        icon_hover: <NavIcon iconName="Roles" state="hover" />,
        label: "Roles Management",
      },
      {
        key: "settings",
        icon: <NavIcon iconName="Settings" />,
        icon_hover: <NavIcon iconName="Settings" state="hover" />,
        label: "Settings",
      },
      {
        key: "settings/support",
        icon: <NavIcon iconName="Support" />,
        icon_hover: <NavIcon iconName="Support" state="hover" />,
        label: "Support",
      },
      {
        key: "settings/help/faq/support",
        icon: <NavIcon iconName="Help" />,
        icon_hover: <NavIcon iconName="Help" state="hover" />,
        label: "Help and FAQ",
      },
    ],
  },
];

const SideMenu = () => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);

  const getMenuItemClassName = (path) => {
    // For the overview menu item (empty path)
    if (path === "") {
      const isActive =
        location.pathname === "/dashboard" ||
        location.pathname === "/dashboard/";
      return `${isActive ? "border-l-[3px] border-l-primary" : ""} 
                    transition-colors duration-200 hover:border-l-[3px] hover:border-l-primary`;
    }

    // For all other menu items
    const isActive = location.pathname.includes(`/dashboard/${path}`);
    return `${isActive ? "border-l-[3px] border-l-primary" : ""} 
                transition-colors duration-200 hover:border-l-[3px] hover:border-l-primary`;
  };

  return (
    <div
      className="sticky top-[64px] flex-col hidden h-[90vh] gap-3 text-left border-r pt-7 lg:flex 
                        border-r-dark-500 overflow-y-auto no-scrollbar transition-colors duration-200"
      style={{ backgroundColor: "var(--sidebar-bg)" }}>
      {menuItems.map((menuItem) => {
        if (menuItem.children) {
          return (
            <div key={menuItem.key}>
              <h1 className="ml-2 font-bold text-medium">{menuItem.label}</h1>
              {menuItem.children.map((childItem) => {
                const isHovered = hoveredItem === childItem.key;
                return (
                  <div
                    key={childItem.key}
                    className={`${getMenuItemClassName(childItem.key)} py-2`}
                    onMouseEnter={() => setHoveredItem(childItem.key)}
                    onMouseLeave={() => setHoveredItem(null)}>
                    <Link
                      to={`/dashboard/${childItem.key}`}
                      className={`flex items-center gap-3 px-2 transition-colors duration-200 ${
                        location.pathname.includes(
                          `/dashboard/${childItem.key}`
                        )
                          ? "text-medium font-bold"
                          : "text-medium"
                      }`}
                      style={{
                        color: location.pathname.includes(
                          `/dashboard/${childItem.key}`
                        )
                          ? "var(--sidebar-text-active)"
                          : "var(--sidebar-text-inactive)",
                      }}>
                      {location.pathname.includes(
                        `/dashboard/${childItem.key}`
                      ) || isHovered
                        ? childItem.icon_hover
                        : childItem.icon}
                      {childItem.label}
                    </Link>
                  </div>
                );
              })}
            </div>
          );
        }

        const isHovered = hoveredItem === menuItem.key;
        return (
          <div
            key={menuItem.key}
            className={`${getMenuItemClassName(menuItem.key)} py-2`}
            onMouseEnter={() => setHoveredItem(menuItem.key)}
            onMouseLeave={() => setHoveredItem(null)}>
            <Link
              to={`/dashboard${menuItem.key ? `/${menuItem.key}` : ``}`}
              className={`flex items-center gap-3 px-2 transition-colors duration-200 ${
                menuItem.key === ""
                  ? location.pathname === "/dashboard" ||
                    location.pathname === "/dashboard/"
                    ? "text-medium font-bold"
                    : "text-medium"
                  : location.pathname.includes(`/dashboard/${menuItem.key}`)
                  ? "text-medium font-bold"
                  : "text-medium"
              }`}
              style={{
                color:
                  menuItem.key === ""
                    ? location.pathname === "/dashboard" ||
                      location.pathname === "/dashboard/"
                      ? "var(--sidebar-text-active)"
                      : "var(--sidebar-text-inactive)"
                    : location.pathname.includes(`/dashboard/${menuItem.key}`)
                    ? "var(--sidebar-text-active)"
                    : "var(--sidebar-text-inactive)",
              }}>
              {(menuItem.key === ""
                ? location.pathname === "/dashboard" ||
                  location.pathname === "/dashboard/"
                : location.pathname.includes(`/dashboard/${menuItem.key}`)) ||
              isHovered
                ? menuItem.icon_hover
                : menuItem.icon}
              {menuItem.label}
            </Link>
          </div>
        );
      })}
    </div>
  );
};

export default SideMenu;
